/**
 * The simulation core. No worker APIs and no DOM, so the identical code runs
 * inside the worker (global graph) and on the main thread (local graph).
 */
import { forceSimulation, type Simulation } from "d3-force";
import {
  ALPHA_MIN,
  PLAYBACK_ALPHA,
  PREWARM_TICKS,
  WARM_ALPHA,
  attachForces,
  configureForces,
  createForces,
  type ForceBundle,
  type ForceOpts,
  type SimLink,
  type SimNode,
} from "./forces";
import type { SetGraphMsg, SimResponse } from "./protocol";

export type PublishMode = "transfer" | "copy";

export interface EngineEmit {
  (msg: SimResponse, transfer?: Transferable[]): void;
}

export class SimEngine {
  private sim: Simulation<SimNode, SimLink>;
  private forces: ForceBundle;
  private nodes: SimNode[] = [];
  private links: SimLink[] = [];
  private pos = new Float32Array(0);
  private free: ArrayBuffer[] = [];
  private epoch = -1;
  private activeNodes = 0;
  private activeLinks = 0;
  private dragIndex = -1;
  private opts: ForceOpts | null = null;
  private settledSent = false;

  constructor(
    private readonly emit: EngineEmit,
    private readonly mode: PublishMode,
  ) {
    this.forces = createForces();
    this.sim = forceSimulation<SimNode, SimLink>([]);
    attachForces(this.sim, this.forces);
    // We tick manually and never let d3-timer drive: in a worker it falls back
    // to setTimeout(f, 17) because it sniffs for `window`.
    this.sim.stop();
  }

  get currentEpoch(): number {
    return this.epoch;
  }

  setGraph(msg: SetGraphMsg): void {
    this.epoch = msg.epoch;
    this.opts = msg.forces;
    this.settledSent = false;
    this.dragIndex = -1;

    const n = msg.nodeCount;
    this.nodes = new Array<SimNode>(n);
    for (let i = 0; i < n; i++) {
      this.nodes[i] = {
        x: msg.pos[i * 2]!,
        y: msg.pos[i * 2 + 1]!,
        vx: 0,
        vy: 0,
        deg: msg.degree[i]!,
      };
    }

    const m = msg.linkSource.length;
    this.links = new Array<SimLink>(m);
    for (let j = 0; j < m; j++) {
      this.links[j] = { source: this.nodes[msg.linkSource[j]!]!, target: this.nodes[msg.linkTarget[j]!]! };
    }

    this.pos = new Float32Array(n * 2);
    this.free = [new ArrayBuffer(n * 8), new ArrayBuffer(n * 8)];

    this.activeNodes = Math.min(msg.activeNodes, n);
    this.activeLinks = Math.min(msg.activeLinks, m);
    this.applyActive();

    configureForces(this.forces, msg.forces);
    if (msg.cold) {
      this.sim.alpha(1);
      for (let i = 0; i < PREWARM_TICKS; i++) this.sim.tick();
    } else {
      this.sim.alpha(Math.max(this.sim.alpha(), WARM_ALPHA));
    }
    this.sim.alphaTarget(0);
    this.publish(true);
  }

  setForces(opts: ForceOpts): void {
    this.opts = opts;
    configureForces(this.forces, opts);
    // a slider nudge should visibly settle, never relayout from scratch
    this.sim.alpha(Math.max(this.sim.alpha(), 0.3));
    this.settledSent = false;
  }

  /** Creation playback: grow the live prefix of nodes and links. */
  setActive(activeNodes: number, activeLinks: number, warm: boolean): void {
    const next = Math.min(activeNodes, this.nodes.length);
    const nextLinks = Math.min(activeLinks, this.links.length);
    if (next === this.activeNodes && nextLinks === this.activeLinks) return;
    this.activeNodes = next;
    this.activeLinks = nextLinks;
    this.applyActive();
    this.sim.alphaTarget(warm ? PLAYBACK_ALPHA : 0);
    if (this.sim.alpha() < PLAYBACK_ALPHA) this.sim.alpha(PLAYBACK_ALPHA);
    this.settledSent = false;
  }

  drag(index: number, x: number, y: number): void {
    const node = this.nodes[index];
    if (!node) return;
    if (this.dragIndex !== index) {
      this.dragIndex = index;
      // BOTH, not just the target: from a settled alpha, alphaTarget alone
      // takes ~45 ticks to warm up, so neighbours would visibly lag the
      // pointer for the first third of a second.
      this.sim.alpha(Math.max(this.sim.alpha(), WARM_ALPHA));
      this.sim.alphaTarget(WARM_ALPHA);
    }
    node.fx = x;
    node.fy = y;
    this.settledSent = false;
  }

  dragEnd(index: number): void {
    const node = this.nodes[index];
    if (node) {
      // no pin on release, matching Obsidian
      node.fx = null;
      node.fy = null;
    }
    this.dragIndex = -1;
    this.sim.alphaTarget(0);
  }

  reheat(alpha: number, target: number): void {
    this.sim.alpha(Math.max(this.sim.alpha(), alpha));
    this.sim.alphaTarget(target);
    this.settledSent = false;
  }

  needsTick(): boolean {
    return this.sim.alpha() > ALPHA_MIN || this.dragIndex >= 0 || this.sim.alphaTarget() > 0;
  }

  tick(): void {
    this.sim.tick();
  }

  alpha(): number {
    return this.sim.alpha();
  }

  /** Reclaim a transferred buffer. Ignores detached or wrong-sized returns. */
  recycle(buffer: ArrayBuffer): void {
    if (buffer.byteLength === 0) return; // detached, e.g. a double transfer
    if (buffer.byteLength !== this.pos.byteLength) return; // sized for an old topology
    if (this.free.length < 2) this.free.push(buffer);
  }

  /**
   * Publish-and-copy: the engine keeps `pos` forever and copies into a
   * recycled buffer, so the renderer never reads transferred memory and a
   * detached buffer can therefore never cost it a frame.
   */
  publish(force = false): void {
    const n = this.nodes.length;
    if (!n) return;
    for (let i = 0; i < n; i++) {
      const node = this.nodes[i]!;
      this.pos[i * 2] = node.x;
      this.pos[i * 2 + 1] = node.y;
    }

    if (this.mode === "copy") {
      this.emit({
        type: "frame",
        epoch: this.epoch,
        count: this.activeNodes,
        pos: this.pos.slice().buffer,
        alpha: this.sim.alpha(),
      });
      return;
    }

    const buf = this.free.pop();
    if (!buf) {
      // main is already behind; another frame does not help it catch up
      if (!force) return;
      const fresh = this.pos.slice();
      this.emit(
        { type: "frame", epoch: this.epoch, count: this.activeNodes, pos: fresh.buffer, alpha: this.sim.alpha() },
        [fresh.buffer],
      );
      return;
    }
    new Float32Array(buf).set(this.pos);
    this.emit(
      { type: "frame", epoch: this.epoch, count: this.activeNodes, pos: buf, alpha: this.sim.alpha() },
      [buf],
    );
  }

  maybeSettled(): void {
    if (this.settledSent || this.needsTick()) return;
    this.settledSent = true;
    this.emit({ type: "settled", epoch: this.epoch });
  }

  destroy(): void {
    this.sim.stop();
    this.nodes = [];
    this.links = [];
    this.free = [];
  }

  private applyActive(): void {
    this.forces.link.links(this.links.slice(0, this.activeLinks));
    this.sim.nodes(this.nodes.slice(0, this.activeNodes));
    if (this.opts) configureForces(this.forces, { ...this.opts, nodeCount: this.activeNodes });
  }
}
